<template>
  <div class="container-fluid page-container">
    <ban-edit-profile v-if="isBannedFromEditing" />
    <div v-else-if="!busy">
      <div class="row page-header__container mb-4 pb-3">
        <page-heading :class="'col align-items-center'" :title="pageTitle" />
      </div>
      <form v-if="workTypes.length > 0" @submit="onSave">
        <div class="row">
          <div class="col-12 order-2 order-md-0">
            <div class="row mb-3">
              <div class="lead-worktype-heading col col-md-8 col-lg-7 px-4">
                <div class="row">
                  <div class="col col-sm-5 col-md-4 col-lg-5 col-xxl-6">
                    <h6>{{ $t('Lead Type') }}</h6>
                  </div>
                  <div class="col col-6 col-sm-4 col-md-4 col-lg-4 col-xxl-3 d-flex lead-cap-column">
                    <h6>
                      {{ $t('Lead Cap') }}  <font-awesome-icon id="lead-cap-info" icon="info-circle" />
                    </h6>
                  </div>
                  <div class="col col-4 col-sm-4 col-lg-3 col-xxl-3 text-center">
                    <h6>{{ $t('How would you like to buy your leads?') }}</h6>
                  </div>
                </div>
              </div>
            </div>

            <b-tooltip target="lead-cap-info" placement="top">
              {{ $t('Shows the maximum number of leads we\'ll offer you for each type of lead (per given period). Simply turn off for unlimited alerts.') }}
            </b-tooltip>
          </div>

          <div class="col col-12 col-12 order-2 order-md-1" :class="[{'col-md-8 col-lg-7': showFirstStage}]">
            <div v-for="(companyWorkTypeData, index) in workTypes"
                 :id="'wt-' + companyWorkTypeData.companyWorkType.id"
                 :key="companyWorkTypeData.companyWorkType.id"
                 class="row pb-3 worktype__container"
            >
              <div class="col col-12 order-2 order-md-1 order-lg-0"
                   :class="[{'worktype__container--pristine' : !currentWorkTypeChanged(companyWorkTypeData.companyWorkType)},
                            showFirstStage ? 'col-12' : (!hasChanges(companyWorkTypeData) ? 'col-md-8 col-lg-7' : 'col-md-12 col-lg-10')]"
              >
                <b-card no-body class="container-fluid bg-white shadow border rounded worktype__card py-md-3 py-xxl-2 fadein-details">
                  <div class="row py-3">
                    <validation-observer v-slot="{validate}" :slim="true" tag="div" class="w-100">
                      <div :class="[!hasChanges(companyWorkTypeData) ? 'col-12' : 'col-12 col-md-9 card__expanded']">
                        <div class="row">
                          <div class="col-12 text-center text-sm-left col-sm-4 col-md-4 col-lg-5 col-xxl-6 align-self-center">
                            <h2 class="lead-type-heading mb-3 mb-sm-0">
                              {{ getDescriptionTranslation(companyWorkTypeData.companyWorkType.work_type) }}
                            </h2>
                          </div>
                          <div class="col-6 col-sm-4 col-md-4 col-lg-4 col-xxl-3 align-self-center lead-cap-column">
                            <div class="row">
                              <div
                                :id="'lead-cap-info' + index"
                                class="col-12 d-block d-sm-none leads-switch-label lead-cap-day text-center"
                              >
                                <font-awesome-icon icon="info-circle" />
                                {{ $t('Lead Cap') }} <br class="d-md-none">
                                <span>{{ $t('(per day)') }}</span>
                              </div>
                              <b-tooltip
                                v-if="isMobile"
                                :target="'lead-cap-info' + index"
                                placement="top"
                              >
                                {{ $t('Shows the maximum number of daily leads we\'ll offer you for each type of lead. Simply turn off for unlimited alerts.') }}
                              </b-tooltip>
                              <div
                                :class="{'worktype__container--disabled': disabled(companyWorkTypeData, 'lead_cap_value')}"
                                class="col d-flex worktype-cap-container flex-fill"
                              >
                                <div
                                  :class="[
                                    'd-flex',
                                    'position-relative',
                                    {'container-highlight'
                                      : isMobile && hasChanges(companyWorkTypeData) && !disabled(companyWorkTypeData, 'lead_cap_value')},
                                  ]"
                                >
                                  <b-checkbox
                                    v-model="companyWorkTypeData.hasCaps"
                                    class="custom-switch-empty"
                                    switch
                                    :disabled="disabled(companyWorkTypeData, 'lead_cap_toggle')"
                                    @input="triggerConfirm('lead_cap_toggle', companyWorkTypeData.companyWorkType.id, $event)"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                          <div
                            :class="{'worktype__container--disabled': disabled(companyWorkTypeData, 'lead_auto_accept')}"
                            class="col-6 col-sm-4 col-md-4 col-lg-3 col-xxl-3 align-self-center"
                          >
                            <div class="row">
                              <div class="col-12 d-block d-sm-none text-center leads-switch-label">
                                {{ $t('How would you like to buy your leads?') }}
                              </div>
                              <div class="col-12">
                                <b-checkbox
                                  v-model="companyWorkTypeData.companyWorkType.allow_autoaccept"
                                  :class="{'container-highlight'
                                    : isMobile && hasChanges(companyWorkTypeData) && !disabled(companyWorkTypeData, 'lead_auto_accept')}"
                                  class="custom-switch-text m-auto"
                                  switch
                                  :disabled="disabled(companyWorkTypeData, 'lead_auto_accept')"
                                  @input="triggerConfirm('lead_auto_accept', companyWorkTypeData.companyWorkType.id, $event)"
                                >
                                  <span class="hide">{{ $t('Automatic') }}</span><span class="show">{{ $t('Manual') }}</span>
                                </b-checkbox>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <hr v-if="hasChanges(companyWorkTypeData)" class="d-md-none text-primary w-75 my-4">

                      <div
                        v-if="hasChanges(companyWorkTypeData)"
                        class="col col-12 col-md-3 px-md-0 autoaccept__container"
                      >
                        <div class="text-center">
                          <p class="small">
                            {{ confirmationMessage }}
                          </p>

                          <div class="d-flex justify-content-center">
                            <b-button
                              size="sm"
                              variant="link"
                              @click.prevent.stop="revertValue(companyWorkTypeData)"
                            >
                              {{ $t('Cancel') }}
                            </b-button>
                            <b-button
                              size="sm"
                              @click.prevent.stop="onSave(validate)"
                            >
                              {{ $t('Confirm') }}
                            </b-button>
                          </div>
                        </div>
                      </div>
                    </validation-observer>
                  </div>
                  <div v-if="companyWorkTypeData.hasCaps" class="row py-3 ">
                    <div
                      :class="[
                        'col-12',
                        'd-flex',
                        'position-relative',
                        {'container-highlight': isMobile && hasChanges(companyWorkTypeData) && !disabled(companyWorkTypeData, 'lead_cap_value')}
                      ]"
                    >
                      <div class="mr-5 cap-name">
                        Daily lead cap <font-awesome-icon id="lead-cap-info-day" icon="info-circle" />
                      </div>
                      <b-tooltip target="lead-cap-info-day" placement="top">
                        {{ $t('Shows the maximum number of leads we\'ll offer you per day for this work type. Simply turn off for unlimited alerts.') }}
                      </b-tooltip>
                      <validation-provider :vid="`validation${index}`">
                        <b-checkbox
                          v-model="companyWorkTypeData.caps.day.toggle"
                          class="custom-switch-empty"
                          switch
                          @input="triggerConfirm('day_cap_toggle', companyWorkTypeData.companyWorkType.id, $event)"
                        />
                      </validation-provider>
                      <div
                        v-if="companyWorkTypeData.caps.day.toggle"
                        class="align-items-baseline d-flex d-md-block lead-cap-value-container ml-1 ml-sm-3"
                      >
                        <validation-provider v-slot="{errors, invalid}" :rules="`numeric|required_if:validation${index},true`">
                          <b-form-input
                            v-model.number.trim="companyWorkTypeData.caps.day.value"
                            size="sm"
                            class="lead-cap-value"
                            @input="triggerConfirm('day_cap_value', companyWorkTypeData.companyWorkType.id, $event)"
                          />
                          <b-form-invalid-feedback class="position-absolute" :state="invalid" :force-show="invalid">
                            {{ errors[0] }}
                          </b-form-invalid-feedback>
                        </validation-provider>
                      </div>
                    </div>
                    <div
                      :class="[
                        'col-12',
                        'my-3',
                        'd-flex',
                        'position-relative',
                        {'container-highlight': isMobile && hasChanges(companyWorkTypeData) && !disabled(companyWorkTypeData, 'lead_cap_value')}
                      ]"
                    >
                      <div class="mr-5 cap-name">
                        Weekly lead cap <font-awesome-icon id="lead-cap-info-week" icon="info-circle" />
                      </div>
                      <b-tooltip target="lead-cap-info-week" placement="top">
                        {{ $t('Shows the maximum number of leads we\'ll offer you per week for this work type. Simply turn off for unlimited alerts.') }}
                      </b-tooltip>
                      <validation-provider :vid="`validation${index}`">
                        <b-checkbox
                          v-model="companyWorkTypeData.caps.week.toggle"
                          class="custom-switch-empty"
                          switch
                          @input="triggerConfirm('week_cap_toggle', companyWorkTypeData.companyWorkType.id, $event)"
                        />
                      </validation-provider>
                      <div
                        v-if="companyWorkTypeData.caps.week.toggle"
                        class="align-items-baseline d-flex d-md-block lead-cap-value-container ml-1 ml-sm-3"
                      >
                        <validation-provider v-slot="{errors, invalid}" :rules="`numeric|required_if:validation${index},true`">
                          <b-form-input
                            v-model.number.trim="companyWorkTypeData.caps.week.value"
                            size="sm"
                            class="lead-cap-value"
                            @input="triggerConfirm('week_cap_value', companyWorkTypeData.companyWorkType.id, $event)"
                          />
                          <b-form-invalid-feedback class="position-absolute" :state="invalid" :force-show="invalid">
                            {{ errors[0] }}
                          </b-form-invalid-feedback>
                        </validation-provider>
                      </div>
                    </div>
                    <div
                      :class="[
                        'col-12',
                        'd-flex',
                        'position-relative',
                        {'container-highlight': isMobile && hasChanges(companyWorkTypeData) && !disabled(companyWorkTypeData, 'lead_cap_value')}
                      ]"
                    >
                      <div class="mr-5 cap-name">
                        Monthly lead cap <font-awesome-icon id="lead-cap-info-month" icon="info-circle" />
                      </div>
                      <b-tooltip target="lead-cap-info-month" placement="top">
                        {{ $t('Shows the maximum number of leads we\'ll offer you per month for this work type. Simply turn off for unlimited alerts.') }}
                      </b-tooltip>
                      <validation-provider :vid="`validation${index}`">
                        <b-checkbox
                          v-model="companyWorkTypeData.caps.month.toggle"
                          class="custom-switch-empty"
                          switch
                          @input="triggerConfirm('month_cap_toggle', companyWorkTypeData.companyWorkType.id, $event)"
                        />
                      </validation-provider>
                      <div
                        v-if="companyWorkTypeData.caps.month.toggle"
                        class="align-items-baseline d-flex d-md-block lead-cap-value-container ml-1 ml-sm-3"
                      >
                        <validation-provider v-slot="{errors, invalid}" :rules="`numeric|required_if:validation${index},true`">
                          <b-form-input
                            v-model.number.trim="companyWorkTypeData.caps.month.value"
                            size="sm"
                            class="lead-cap-value"
                            @input="triggerConfirm('month_cap_value', companyWorkTypeData.companyWorkType.id, $event)"
                          />
                          <b-form-invalid-feedback class="position-absolute" :state="invalid" :force-show="invalid">
                            {{ errors[0] }}
                          </b-form-invalid-feedback>
                        </validation-provider>
                      </div>
                    </div>
                  </div>
                </b-card>
              </div>

              <template v-if="worktypeSlideOut(index, companyWorkTypeData.companyWorkType)">
                <div
                  v-if="wizardEnabled && wizardStage === 1 || wizardStage === 2"
                  class="col-12 col-md-12 col-lg-2 order-1 d-flex"
                >
                  <b-card
                    v-if="!isMobile"
                    no-body
                    class="bg-white d-flex border card h-100 justify-content-center m-auto rounded shadow px-2 w-100 p-md-2 p-lg-0 my-md-2 my-lg-0 text-center"
                  >
                    <p class="small mb-0 px-1">
                      {{ wizardStage === 1 ? $t('Flip the toggle switch to begin'): '' }}
                      {{ wizardStage === 2 ? $t('If you\'re happy to buy leads automatically, hit the \'Confirm\' button') : '' }}
                    </p>
                  </b-card>
                  <b-card
                    v-else
                    no-body
                    class="card w-100 border rounded bg-white align-items-center justify-content-center px-4 py-3 mb-3"
                  >
                    <p class="small mb-0 px-1">
                      {{ wizardStage === 1 ? $t('Flip the toggle switch to begin'): '' }}
                      {{ wizardStage === 2 ? $t('If you\'re happy to buy leads automatically, hit the \'Confirm\' button') : '' }}
                    </p>
                  </b-card>
                </div>
                <div
                  v-if="wizardStage === 3"
                  class="col col-12 col-md-4 col-lg-5 order-0 order-md-1 mb-3 mb-md-0 mt-3 mt-md-0 align-items-center justify-content-center"
                  @click="wizardStage = 0"
                >
                  <b-card
                    no-body
                    class="bg-white border m-auto rounded shadow p-4 text-center wizard__container"
                  >
                    <h2 class="h3 pt-3">
                      {{ $t('Great! Autoaccept is now active for this lead type.') }}
                    </h2>
                    <p class="py-3 px-3 font-weight-normal">
                      {{ $t('Our system will buy the latest leads automatically for you, so you don\'t miss out!') }}
                    </p>
                    <h3 class="h6">
                      {{ $t('Why not activate Auto Accept for other lead types?') }}
                    </h3>
                  </b-card>
                </div>
              </template>
            </div>
          </div>

          <div
            v-if="showFirstStage"
            class="col col-12 col-md-4 col-lg-5 order-0 order-md-1 mb-3 mb-md-0"
          >
            <b-card
              no-body
              class="bg-white border rounded shadow m-auto overflow-hidden autoaccept__start"
            >
              <div
                class="card-top"
                :style="{ background: `url('/images/lead-settings/autoaccept-top-background.png')`}"
              >
                <div class="text-center d-flex justify-content-center flex-column pt-4">
                  <img
                    :src="'/images/lead-settings/autoaccept-clock.png'"
                    width="97"
                    height="68"
                    class="m-auto"
                    alt="Clock Icon"
                  >
                  <h2 class="h3 pt-4 text-white font-weight-500">
                    {{ $t('Get ahead of the rest with') }}
                  </h2>
                  <h3 class="h2 pb-3 font-weight-bolder text-white">
                    {{ $t('AUTO ACCEPT') }}
                  </h3>
                </div>
              </div>
              <div class="px-3 py-4 text-center">
                <p class="py-3 px-3 font-weight-500">
                  {{ $t('Receiving your leads automatically gets you to your customer quicker. Winning the work just got easier!') }}
                </p>
                <div class="my-3 w-100">
                  <b-button
                    class="m-auto py-1"
                    variant="outline-secondary"
                    @click="wizardStage = 1"
                  >
                    {{ $t('Get Started') }}
                  </b-button>
                </div>
              </div>
            </b-card>
          </div>
        </div>
      </form>
      <div
        v-else
        class="row vh-70"
      >
        <b-col class="col-sm-7 m-auto">
          <b-card :body-class="['shadow', 'border', 'rounded', 'py-5', 'text-center']">
            <b-card-text>
              <h2 class="mb-0 h3">
                {{ $t('You have not added any lead types') }}
              </h2>
              <hr class="w-50">
              <router-link :class="'text-link'" :to="{name: 'lead-selection-&-coverage-tab-0'}">
                {{ $t('You can add lead types in Coverage') }}
              </router-link>
            </b-card-text>
          </b-card>
        </b-col>
      </div>
    </div>
    <spinner v-else />
  </div>
</template>

<script>
import PageHeading from "@/components/control/PageHeading";
import BanEditProfile from "@/components/control/BanEditProfile";
import Spinner from "@/components/animation/Spinner";
import NotificationMixin from '@/imports/mixins/notification';
import isBannedFromEditing from "@/imports/mixins/isBannedFromEditing";
import userDevice from "@/imports/mixins/userDevice";
import {ValidationObserver, ValidationProvider} from 'vee-validate';
import company from "@/imports/mixins/company";
import workTypeTranslation from "@/imports/mixins/workTypeTranslation";

const CAP_TOGGLES = ['day_cap_toggle', 'week_cap_toggle', 'month_cap_toggle'];
const CAP_VALUES = ['day_cap_value', 'week_cap_value', 'month_cap_value'];

export default {
  name: "LeadSettings",
  components: {PageHeading, Spinner, BanEditProfile,ValidationObserver, ValidationProvider},
  mixins: [NotificationMixin, isBannedFromEditing, userDevice, company, workTypeTranslation],
  data() {
    return {
      busy: false,
      wizardStage: 0,
      workTypes: [],
      isSaving: false,
      change: {},
      original: {}
    }
  },
  computed: {
    showFirstStage: function() {
      return this.wizardEnabled && (this.wizardStage === 0) && !this.changePending;
    },
    wizardEnabled: function() {
      return (this.original.length === 0)
        ? false
        : (this.original.filter((workType) => workType.allow_autoaccept === true).length < 1);
    },
    confirmationMessage: function () {
      if (this.change) {
        switch (this.change.type) {
          case 'day_cap_toggle':
            return (this.change.value === true) ? this.$t("Daily lead cap will be enabled") : this.$t("Daily lead cap will be disabled");
          case 'day_cap_value':
            return this.$t("This will change the value of your daily lead cap");
          case 'week_cap_toggle':
            return (this.change.value === true) ? this.$t("Weekly lead cap will be enabled") : this.$t("Weekly lead cap will be disabled");
          case 'week_cap_value':
            return this.$t("This will change the value of your weekly lead cap");
          case 'month_cap_toggle':
            return (this.change.value === true) ? this.$t("Monthly lead cap will be enabled") : this.$t("Monthly lead cap will be disabled");
          case 'month_cap_value':
            return this.$t("This will change the value of your monthly lead cap");
          case 'lead_auto_accept':
            return (this.change.value === true) ? this.$t("Automatically buy this lead type.") : this.$t("This will disable lead auto accept");
        }
      }
      return null;
    },
    changePending: function() {
      return Object.values(this.change).length > 0;
    },
    pageTitle() {
      return this.$t("Lead Delivery");
    }
  },
  methods: {
    triggerConfirm: function(type, element, value) {
      const originalCWT = Object.values(this.original).find((companyWorkTypeData) => companyWorkTypeData.companyWorkType.id === element);
      let hasChange = false;
      switch (type) {
        case 'day_cap_toggle':
          hasChange = (originalCWT.caps.day.toggle !== value);
          break;
        case 'day_cap_value':
          hasChange = (originalCWT.caps.day.value !== parseInt(value));
          break;
        case 'week_cap_toggle':
          hasChange = (originalCWT.caps.week.toggle !== value);
          break;
        case 'week_cap_value':
          hasChange = (originalCWT.caps.week.value !== parseInt(value));
          break;
        case 'month_cap_toggle':
          hasChange = (originalCWT.caps.month.toggle !== value);
          break;
        case 'month_cap_value':
          hasChange = (originalCWT.caps.month.value !== parseInt(value));
          break;
        case 'lead_auto_accept':
          hasChange = (originalCWT.companyWorkType.allow_autoaccept !== value);
      }
      this.change = hasChange ? {type, element, value} : {};
    },
    hasChanges: function (companyWorkTypeData, inputType = null) {
      if (!this.changePending) {
        return false;
      }

      const currentWorkTypeId = this.change.element === companyWorkTypeData.companyWorkType.id;
      const currentWorktypeInputChanged = inputType === this.change.type;
      const currentLeadCapChanged = ((inputType !== null && CAP_VALUES.includes(inputType)) && CAP_TOGGLES.includes(this.change.type));
      const leadTypeHasChange = currentWorkTypeId ? (currentWorktypeInputChanged || inputType === null || currentLeadCapChanged) : false;

      if (leadTypeHasChange && this.isMobile) {
        this.scrollCardIntoView('wt-' + companyWorkTypeData.companyWorkType.id);
      }

      return leadTypeHasChange;
    },
    revertValue() {
      this.workTypes = JSON.parse(JSON.stringify(this.original));
      this.change = {};
    },
    disabled: function(companyWorkTypeData, inputType) {
      return (this.changePending) ? !this.hasChanges(companyWorkTypeData, inputType) : false;
    },
    onSave(validate) {
      validate().then((success) => {
        if (!success) {
          return;
        }
        this.isSaving = true;
        this.$api.company.updateLeadDeliverySettings(this.workTypes)
          .then(() => {
            this.original = JSON.parse(JSON.stringify(this.workTypes));
            if (this.change.type === 'lead_auto_accept' && this.change.value === true) {
              this.wizardStage = 3;
            }
            this.notify(this.$bvToast, this.$t('Successfully updated your lead delivery settings'), null, 'success');
          }).catch(() => {
            this.notify(this.$bvToast, this.$t('There was an error whilst updating your lead delivery settings'), null, 'danger');
          }).finally(() => {
            this.isSaving = false;
            this.change = {};
          });
      })
    },
    currentWorkTypeChanged: function(worktype) {
      return (worktype.id === this.change.element) || Object.values(this.change).length === 0;
    },
    worktypeSlideOut: function(index, workType) {
      const firstAutoAcceptStep = this.wizardStage > 0;

      if (this.wizardStage === 2 && this.currentWorkTypeChanged(workType)) {
        return true;
      } else if (firstAutoAcceptStep && this.currentWorkTypeChanged(workType)) {
        return index === 0;
      }

      return false;
    },
    scrollCardIntoView: function(identifier) {
      setTimeout(() => {
        document.getElementById(identifier).scrollIntoView(true);
        window.scrollBy(0, -70);
      }, 10);
    },
    setCapToggles(data) {
      return data.map(companyWorkTypeData => {
        companyWorkTypeData.hasCaps = companyWorkTypeData.caps.day.toggle
          || companyWorkTypeData.caps.week.toggle
          || companyWorkTypeData.caps.month.toggle;
        return companyWorkTypeData;
      })
    }
  },
  watch: {
    change: function(value) {
      if (value.type === 'lead_auto_accept') {
        this.wizardStage = value.value === true ? 2 : 1;
      } else {
        this.wizardStage = 0;
      }
    }
  },
  created() {
    this.busy = true;
    this.$api.company.getLeadDeliverySettings().then((result) => {
      this.workTypes = this.setCapToggles(result.data.data);
      this.original = JSON.parse(JSON.stringify(this.workTypes));
      this.busy = false;
    });
  },
}
</script>

<style lang="scss">
@media only screen and (max-width: 575px) {
  .worktype-cap-container {
    justify-content: center;
  }
}

.lead-cap {
  &-value {
    display: inline-block;
    width: 5em;

    &-container {
      @include media-breakpoint-up(sm) {
        width: 70px;
      }

      input {
        width: 45px;
        height: 33px;
      }

      .lead-cap-value {
        @include font-size(.85rem);
      }
    }
  }
}

.card {
  .autoaccept__container {

    @include media-breakpoint-up(md) {
      position: absolute;
      right: 10px;
    }

    @include media-breakpoint-between(md, xl) {
      top: 20px;
      right: 25px;
    }

    @media only screen and (min-width: 1400px) and (max-width: 1813px) {
      top: 5px;
    }

    @media only screen and (min-width: 1814px) {
      top: 15px;
    }
  }
  &-top {
    background-size: cover !important;
    background-repeat: no-repeat !important;
  }
}

.worktype__card {
  .lead-type-heading {
    font-size: $font-size-15;
  }
  .cap-name {
    min-width: 200px;
    @include media-breakpoint-down(sm) {
      min-width: 130px;
      margin-right: 1rem !important;
    }
    font-size: $font-size-14;
  }
}

.lead-cap-value-container {
  .invalid-feedback {
    position: absolute;
    left: 0;
    @include media-breakpoint-down(sm) {
      font-size: 11px;
    }
  }
}

@include media-breakpoint-down(sm) {
  .lead-worktype-heading {
    display: none;
  }

  .worktype__card {

    .leads-switch-label {
      height: 60px;
      max-width: 150px;
      margin: 0 auto;
    }

    .lead-type-heading,
    .lead-cap-day,
    .leads-switch-label {
      font-weight: $font-weight-bold;
    }
  }

  .lead-cap-value-container {
    float: left;
  }

  .worktype__description {
    h2 {
      @include font-size($font-size-15);
    }
  }
}

@include media_breakpoint-up(md) {
  .worktype__container {
    .lead-type-heading {
      font-weight: $font-weight-bold;
    }
  }
}

.autoaccept__container {
  .small {
    @include media-breakpoint-only(md) {
      margin-bottom: 7px;
    }
    @include media-breakpoint-between(lg, xl) {
      margin-bottom: 5px;
    }
    @include media-breakpoint-only(xxl) {
      margin-bottom: 3px;
    }
  }
}

@include media-breakpoint-up(md) {
  .worktype__description {
    margin-left: 20px;
  }

  .worktype__container {
    .wizard__container {
      position: absolute;
      right: 0;
      left: 0;

      @include media-breakpoint-down(md) {
        max-width: 220px;
      }
    }
    &--pristine {
      opacity: .5;
    }
    &--disabled {
      opacity: .5;
      pointer-events: none;
    }
  }

  .wizard__container,
  .autoaccept__start {
    max-width: 360px;
  }

  .worktype__card {
    .card__expanded {
      flex: 0 0 68.888%;
      max-width: 68.888%;
    }
  }

}

@media only screen and (min-width: 1400px) and (max-width: 1545px) {
  .lead-worktype-heading,
  .worktype__container {
    .lead-cap-column {
      padding-right: 5px;
      padding-left: 5px;
    }
  }
}

</style>
